import { BriefCreatorTypeEnum, RequestTypeEnum } from "@nlc/shared-types";
import { Separator } from "react-aria-components";
import { FormProvider } from "react-hook-form";

import { FullPageLoader } from "@/components/fullpage-loader";
import { useCharacterLimitToast } from "@/features/request/create/shared/useCharacterLimitToast";

import { AssetsSection } from "../shared/assets-section";
import { Footer } from "../shared/components/Footer";
import { Header } from "../shared/components/Header";
import { SubmitWarningModal } from "../shared/components/SubmitWarningModal";
import { isAiStudioSubcategorySelected } from "../shared/isAiStudioSubcategorySelected";
import { useAutoSaveDraft } from "../shared/useAutoSaveDraft.tsx";
import { useCreateDraft } from "../shared/useCreateDraft";
import { useDraftRequestId } from "../shared/useDraftRequestId.tsx";
import { useIsMBCSubmitted } from "../shared/useIsMBCSubmitted";
import { useLoadDraftData } from "../shared/useLoadDraftData.tsx";
import { BriefCreatorSection } from "./components/brief-creator-section";
import { IntroSection } from "./components/intro-section";
import { useGetVideoTypes } from "./components/intro-section/useGetVideoTypes";
import { TechnicalDetailsSection } from "./components/technical-details-section";
import {
  CHARACTER_LIMIT_FIELDS,
  useCreateVideoRequestForm,
} from "./components/useCreateVideoRequestForm";
import { useDuplicateVideoRequest } from "./hooks/useDuplicateVideoRequest";
import { useSaveDraftMutation } from "./hooks/useSaveDraftMutation";
import { useSubmitMutation } from "./hooks/useSubmitMutation";

export function CreateVideoRequestForm() {
  const { setShowWarningModal, showWarningModal, ...formMethods } =
    useCreateVideoRequestForm();

  const { data: subcategories } = useGetVideoTypes();
  const isAiStudio = isAiStudioSubcategorySelected(
    subcategories,
    formMethods.watch("videoType"),
  );

  const isMBCSubmitted = useIsMBCSubmitted((state) => state.isSubmitted);

  useCharacterLimitToast({
    fields: CHARACTER_LIMIT_FIELDS,
    formMethods,
  });

  const { isPending: isCreateDraftPending } = useCreateDraft();
  const { isInDuplicateMode, isPending: isDuplicatePending } =
    useDuplicateVideoRequest({
      reset: formMethods.reset,
    });

  useLoadDraftData({
    formMethods: formMethods,
    requestType: RequestTypeEnum.VIDEO,
  });

  const draftId = useDraftRequestId();

  const submitMutation = useSubmitMutation({
    draftId,
    formMethods,
  });

  const saveMutation = useSaveDraftMutation({
    draftId,
    formMethods,
  });

  useAutoSaveDraft({
    draftId,
    formMethods,
    isCreateDraftPending,
    isSubmitting: submitMutation.isPending,
    saveMutation,
  });

  async function onSubmit() {
    const formData = formMethods.getValues();

    if (
      !isMBCSubmitted &&
      !isInDuplicateMode &&
      formData.briefCreatorType === BriefCreatorTypeEnum.DETAILED
    ) {
      setShowWarningModal(true);

      return;
    }

    if (
      isInDuplicateMode &&
      !formData.briefCreatorDetailed.finalBriefHTML &&
      formData.briefCreatorType === BriefCreatorTypeEnum.DETAILED
    ) {
      setShowWarningModal(true);

      return;
    }

    submitMutation.mutate(formData);
  }

  const isPending =
    isDuplicatePending || isCreateDraftPending || submitMutation.isPending;

  return (
    <>
      {isPending && <FullPageLoader />}
      <FormProvider {...formMethods}>
        <div className="relative flex">
          <form
            className="mx-auto flex w-full flex-col rounded-xl bg-bg-primary"
            onSubmit={formMethods.handleSubmit(onSubmit)}
          >
            <Header
              isAiStudio={isAiStudio}
              requestName={formMethods.watch("requestName")}
              requestType="video"
            />
            <div className="flex-1">
              <div className="flex flex-col gap-3xl px-xl lg:px-3xl py-2xl">
                <IntroSection />
                <Separator className="border-border-secondary" />
                <BriefCreatorSection />
                <Separator className="border-border-secondary" />
                <AssetsSection formMethods={formMethods} />
                <Separator className="border-border-secondary" />
                <TechnicalDetailsSection />
              </div>
            </div>
            <Footer />
          </form>
        </div>
      </FormProvider>
      <SubmitWarningModal
        isOpen={showWarningModal}
        onOpenChange={setShowWarningModal}
      />
    </>
  );
}
